import type { PrintEdition, PrintFormat } from "../printProducts";

// Mockup scenes for AOTD prints. Each is a photo made once (Codex) with a flat green blank where the
// product goes, keyed by _print/mockups/key.py into four layers under public/collectibles/scenes/:
//   <name>-base.webp   the photo with the green painted out
//   <name>-light.webp  multiply: the room's white, its falloff, the frame's shadow on the paper
//   <name>-sheen.webp  screen: satin on paper, glare on plastic
//   <name>-over.webp   what sits on top of the print (a thumb), only when `over` is set
// The corners (TL, TR, BR, BL, in photo pixels) are where the print face is bent to. GENERATED from
// _print/mockups/web/scenes/scenes.json; rerun the key step and regenerate rather than editing by hand.

export type SceneName =
  | "poster-sofa"
  | "poster-lean"
  | "wallcard-shelf"
  | "wallcard-desk"
  | "gallery-wall"
  | "card-hand"
  | "card-toploader"
  | "binder-page";

export interface Scene {
  width: number;
  height: number;
  /** a layer of things over the print (fingers) */
  over: boolean;
  /** how soft the photo is, in photo px: the face is blurred to match */
  soft: number;
  /** one quad per print in the scene, in reading order; several = a collection of days */
  targets: [number, number][][];
  label: string;
}

export const SCENES: Record<SceneName, Scene> = {
  "poster-sofa": { width: 1254, height: 1254, over: false, soft: 0.55, label: "on the wall",
    targets: [[[412.0,166.0],[839.0,167.0],[839.0,589.0],[412.0,590.0]]] },
  "poster-lean": { width: 1254, height: 1254, over: false, soft: 0.55, label: "leaning",
    targets: [[[133.0,205.5],[680.8,246.3],[747.0,848.0],[196.0,979.0]]] },
  "wallcard-shelf": { width: 1254, height: 1254, over: false, soft: 0.5, label: "on a shelf",
    targets: [[[458.5,235.0],[1108.0,201.0],[1101.0,927.8],[449.0,890.8]]] },
  "wallcard-desk": { width: 1254, height: 1254, over: false, soft: 0.5, label: "on a desk",
    targets: [[[537.0,208.0],[1091.4,243.0],[1029.4,876.1],[490.0,783.0]]] },
  "gallery-wall": { width: 1254, height: 1254, over: false, soft: 0.6, label: "a wall of nine",
    targets: [[[240.0,96.0],[459.0,96.0],[459.0,310.0],[239.0,310.0]],[[518.0,96.0],[739.0,96.0],[738.0,310.0],[517.0,309.0]],[[798.0,96.0],[1019.0,96.0],[1018.0,310.0],[798.0,309.0]],[[239.0,370.0],[459.0,370.0],[459.0,583.0],[239.0,583.0]],[[517.0,370.0],[739.0,370.0],[739.0,583.0],[517.0,582.0]],[[798.0,370.0],[1019.0,370.0],[1018.0,583.0],[799.0,583.0]],[[239.0,642.0],[459.0,642.0],[459.0,856.0],[239.0,856.0]],[[518.0,642.0],[739.0,642.0],[738.0,856.0],[517.0,855.0]],[[799.0,642.0],[1019.0,643.0],[1018.0,856.0],[799.0,856.0]]] },
  "card-hand": { width: 1254, height: 1254, over: true, soft: 0.35, label: "in the hand",
    targets: [[[341.0,177.0],[957.0,190.0],[942.9,1057.9],[385.0,1060.7]]] },
  "card-toploader": { width: 1254, height: 1254, over: true, soft: 0.5, label: "in a toploader",
    targets: [[[287.0,373.0],[722.4,237.8],[941.3,847.5],[478.2,985.9]]] },
  "binder-page": { width: 1254, height: 1254, over: false, soft: 0.6, label: "in the binder",
    targets: [[[665.0,268.0],[813.0,269.0],[812.0,481.0],[665.0,478.0]],[[836.0,272.0],[984.0,269.0],[986.0,478.0],[836.0,481.1]],[[1008.0,272.0],[1153.0,268.0],[1157.0,478.0],[1012.0,481.0]],[[665.0,512.1],[811.0,508.0],[815.0,719.0],[665.0,722.1]],[[839.0,511.0],[982.0,508.0],[986.0,719.0],[841.0,722.0]],[[1008.0,513.0],[1155.0,509.0],[1157.0,720.0],[1014.0,723.0]],[[665.0,754.1],[811.0,749.0],[815.0,962.0],[665.0,964.1]],[[839.0,753.0],[983.0,750.0],[986.0,962.0],[842.0,964.0]],[[1009.0,754.0],[1155.0,750.0],[1159.0,960.0],[1012.0,963.0]]] },
};

/** The scenes for each format, the first is the main one. Originals use only the single-print scenes. */
export const SCENES_FOR: Record<PrintFormat, Partial<Record<PrintEdition, SceneName[]>>> = {
  poster: { print: ["poster-sofa", "poster-lean"] },
  "wall-card": { print: ["wallcard-shelf", "wallcard-desk", "gallery-wall"], original: ["wallcard-shelf"] },
  "trading-card": { print: ["card-hand", "card-toploader", "binder-page"], original: ["card-toploader", "card-hand"] },
};

export const layerUrl = (scene: SceneName, layer: "base" | "light" | "sheen" | "over") =>
  `${process.env.PUBLIC_URL}/collectibles/scenes/${scene}-${layer}.webp`;
